import { ThunkAction } from 'redux-thunk';

import UnauthenticatedUserSessionModel from 'gen-thrift/unauthenticated_user_session_Model_types';

import { IUserAccountInfoReader, UserAccountInfo } from 'api/UserAccount/interfaces/IUserAccountInfoReader';
import { IAccountRetailerData } from 'api/UserAccount/model/AccountInfo';
import { UserAccountId } from 'api/UserAccount/model/UserAccountId';
import { UserSessionId } from 'api/UserAccount/model/UserSessionId';

import { AccountSessionNotFoundException } from 'shared/lib/account/exceptions/AccountSessionNotFoundException';
import { UnknownCookieException } from 'shared/lib/cookie/exceptions/UnknownCookieException';
import { RuntimeException } from 'shared/lib/general/exceptions/RuntimeException';

import { IPublicContentViewEventService } from 'api/PublicContent/interfaces/IPublicContentViewEventService';
import { PublicContentUrl } from 'api/PublicContent/model/PublicContentUrl';
import { PublicContentViewEventTypeEnum } from 'api/PublicContent/model/PublicContentViewEventTypeEnum';

import { UserAccountManagerImpl } from 'shared/lib/account/impl/UserAccountManagerImpl';
import { UserPasswordManagerImpl } from 'shared/lib/account/impl/UserPasswordManagerImpl';
import { UserSessionManagerImpl } from 'shared/lib/account/impl/UserSessionManagerImpl';
import { ICookieReader } from 'shared/lib/cookie/interfaces/ICookieReader';
import { UnauthenticatedUserSessionManagerImpl } from 'shared/lib/unauthenticated_user_session/impl/UnauthenticatedUserSessionManagerImpl';

import { DjangoApiManager } from 'shared/api/DjangoApiManager';

import { ISignUpData } from 'shared/components/SignUpOrSignIn/ISignUpData';
import { IAction, IActionCreatorsMapObject } from 'shared/models/IAction';

import { ILoggedInUser, ISignUpAndLoginButtonsState, IUserInfo } from './reducers';

import { Observer } from 'shared/utils/observer';
import { getPublicContentParameters } from '../utils';
import { BevSpotDispatch } from 'shared/components/Provider';

export interface ISignUpAndLoginButtonsStore {
    signUpAndLoginButtonsState : ISignUpAndLoginButtonsState;
}

export const ActionTypes = {
    SET_USER_ICON_POPOVER_IS_SHOWN : 'PUBLIC_SITE/SET_USER_ICON_POPOVER_IS_SHOWN',
    SET_UNAUTHENTICATED_USER_SESSION_ID : 'PUBLIC_SITE/SET_UNAUTHENTICATED_USER_SESSION_ID',
    SET_LOGGED_IN_USER : 'PUBLIC_SITE/SET_LOGGED_IN_USER',
    SET_LOGGED_IN_USER_INFO : 'PUBLIC_SITE/SET_LOGGED_IN_USER_INFO',
};

export namespace SignUpAndLoginButtonsActionInterfaces {
    export interface ISetUserIconPopoverIsShown extends IAction {
        payload : {
            isShown : boolean;
        };
    }

    export interface ISetLoggedInUser extends IAction {
        payload : {
            loggedInUser : null | ILoggedInUser;
        };
    }

    export interface ISetLoggedInUserInfo extends IAction {
        payload : {
            userInfo : null | IUserInfo;
        };
    }

    export interface ISetUnauthenticatedUserSessionId extends IAction {
        payload : {
            unauthenticatedUserSessionId : UnauthenticatedUserSessionModel.UnauthenticatedUserSessionId;
        };
    }

    export interface ISignUpAndLoginButtonsActionCreatorsMapObject extends IActionCreatorsMapObject {
        onMount : () => ThunkAction<void, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}>;
        onSetUserIconPopoverIsShown : (
            isShown : boolean,
        ) => ThunkAction<void, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}>;
        onSuccessfulSignIn : () => ThunkAction<Promise<void>, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}>;
        onSuccessfulSignUp : (
            userAccountIdentifier : UserAccountId,
            signUpData : ISignUpData,
        ) => ThunkAction<Promise<void>, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}>;
        onLogout : () => ThunkAction<Promise<void>, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}>;
    }

    export interface IServices {
        cookieReader : ICookieReader;
        publicContentViewEventService : IPublicContentViewEventService;
        unauthenticatedUserSessionManager : UnauthenticatedUserSessionManagerImpl;
        userSessionManager : UserSessionManagerImpl;
        userAccountInfoReader : IUserAccountInfoReader;
        userAccountManager : UserAccountManagerImpl;
        userPasswordManager : UserPasswordManagerImpl;
        djangoApiManager : DjangoApiManager;
    }
}

export type SignUpAndLoginDispatch = BevSpotDispatch<ISignUpAndLoginButtonsStore, { services: SignUpAndLoginButtonsActionInterfaces.IServices }>

const setUnauthenticatedUserSessionId = (
    unauthenticatedUserSessionId : UnauthenticatedUserSessionModel.UnauthenticatedUserSessionId,
) : SignUpAndLoginButtonsActionInterfaces.ISetUnauthenticatedUserSessionId => ({
    payload : {
        unauthenticatedUserSessionId,
    },
    type : ActionTypes.SET_UNAUTHENTICATED_USER_SESSION_ID,
});

const setLoggedInUser = (
    loggedInUser : null | ILoggedInUser,
) : SignUpAndLoginButtonsActionInterfaces.ISetLoggedInUser => ({
    payload : {
        loggedInUser,
    },
    type : ActionTypes.SET_LOGGED_IN_USER,
});

const setLoggedInUserInfo = (
    userInfo : null | IUserInfo,
) : SignUpAndLoginButtonsActionInterfaces.ISetLoggedInUserInfo => ({
    payload : {
        userInfo,
    },
    type : ActionTypes.SET_LOGGED_IN_USER_INFO,
});

const setUserIconPopoverIsShown = (
    isShown : boolean,
) : SignUpAndLoginButtonsActionInterfaces.ISetUserIconPopoverIsShown => ({
    payload : {
        isShown,
    },
    type : ActionTypes.SET_USER_ICON_POPOVER_IS_SHOWN,
});

const getLoggedInUserIdAndUserInfo = (
    authenticatedUserSessionId : UserSessionId
) : ThunkAction<Promise<void | SignUpAndLoginButtonsActionInterfaces.ISetLoggedInUserInfo>, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : Promise<void | SignUpAndLoginButtonsActionInterfaces.ISetLoggedInUserInfo> => {
        return (extraArguments.services.userSessionManager.getAccountId()
        .then((userId : UserAccountId) => {
            dispatch(setLoggedInUser({
                userId,
                authenticatedUserSessionId,
                userInfo: null,
            }));

            return extraArguments.services.userAccountInfoReader.getAccountInfo(userId)
            .then((accountInfo : UserAccountInfo) => {
                return extraArguments.services.userAccountInfoReader.getAccountRetailers(userId)
                .then((retailers : IAccountRetailerData) => {
                    return dispatch(setLoggedInUserInfo({
                        firstName: accountInfo.firstName,
                        lastName: accountInfo.lastName,
                        emailAddress : accountInfo.emailAddress,
                        phoneNumber : accountInfo.phoneNumber,
                        hasAccessibleRetailers : (retailers.retailerIds.length > 0),
                    }));
                });
            });
        }));
    };
};

const onMount = () : ThunkAction<void, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : void => {
        let authenticatedUserSessionId : UserSessionId | null = null;
        try {
            authenticatedUserSessionId = extraArguments.services.userSessionManager.getSessionId();
        } catch (error) {
            if (!(error instanceof AccountSessionNotFoundException)) {
                throw error;
            }
        }

        const publicContentParameters = getPublicContentParameters(window.location.href);

        extraArguments.services.unauthenticatedUserSessionManager.getOrCreateUnauthenticatedUserSessionId()
        .then((unauthenticatedUserSessionId : UnauthenticatedUserSessionModel.UnauthenticatedUserSessionId) => {
            dispatch(setUnauthenticatedUserSessionId(unauthenticatedUserSessionId));
            return unauthenticatedUserSessionId;
        })
        .then((unauthenticatedUserSessionId : UnauthenticatedUserSessionModel.UnauthenticatedUserSessionId) => {
            if (authenticatedUserSessionId === null) {
                dispatch(setLoggedInUser(null));
                Observer.identifyUnauthenticated(unauthenticatedUserSessionId);
                Observer.observePageView();

                extraArguments.services.publicContentViewEventService.createPublicContentViewEventForUnauthenticatedSession(
                    unauthenticatedUserSessionId,
                    PublicContentViewEventTypeEnum.PAGE_VIEW,
                    new PublicContentUrl('www.bevspot.com', window.location.pathname),
                    publicContentParameters.publicContentUrlParameters,
                    document.referrer,
                );
            } else {
                dispatch(getLoggedInUserIdAndUserInfo(authenticatedUserSessionId))
                .then(() => {
                    const loggedInUser : ILoggedInUser | null | undefined = getState().signUpAndLoginButtonsState.loggedInUser;
                    if (!loggedInUser) {
                        throw new RuntimeException();
                    }

                    const userId : UserAccountId = loggedInUser.userId;
                    const userInfo : IUserInfo = loggedInUser.userInfo as IUserInfo;
                    Observer.identifyAuthenticated(userId, userInfo.firstName + ' ' + userInfo.lastName, null, null); // TODO: no email in this context
                    Observer.observePageView();
                });

                extraArguments.services.publicContentViewEventService.createPublicContentViewEventForAuthenticatedSession(
                    authenticatedUserSessionId,
                    PublicContentViewEventTypeEnum.PAGE_VIEW,
                    new PublicContentUrl('www.bevspot.com', window.location.pathname),
                    publicContentParameters.publicContentUrlParameters,
                    document.referrer,
                );
            }
        })
        .catch((error) => {
            throw error;
        });
    };
};

const onSetUserIconPopoverIsShown = (
    isShown : boolean,
) : ThunkAction<void, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : void => {
        dispatch(setUserIconPopoverIsShown(isShown));
    };
};

const onSuccessfulSignIn = () : ThunkAction<Promise<void>, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : Promise<void> => {
        return Promise.resolve().then(() => {
            let authenticatedUserSessionId : UserSessionId | null = null;
            try {
                authenticatedUserSessionId = extraArguments.services.userSessionManager.getSessionId();
            } catch (error) {
                if (!(error instanceof AccountSessionNotFoundException)) {
                    throw error;
                }
            }

            if (authenticatedUserSessionId === null) {
                throw new RuntimeException('authenticatedUserSessionId unexpectedly null');
            }

            const publicContentParameters = getPublicContentParameters(window.location.href);

            extraArguments.services.publicContentViewEventService.createPublicContentViewEventForAuthenticatedSession(
                authenticatedUserSessionId,
                PublicContentViewEventTypeEnum.PAGE_VIEW,
                new PublicContentUrl('www.bevspot.com', window.location.pathname),
                publicContentParameters.publicContentUrlParameters,
                document.referrer,
            );

            dispatch(getLoggedInUserIdAndUserInfo(authenticatedUserSessionId))
            .then(() => {
                const loggedInUser : ILoggedInUser | null | undefined = getState().signUpAndLoginButtonsState.loggedInUser;
                if (!loggedInUser) {
                    throw new RuntimeException();
                }

                const userId : UserAccountId = loggedInUser.userId;
                const userInfo : IUserInfo = loggedInUser.userInfo as IUserInfo;
                Observer.identifyAuthenticated(userId, userInfo.firstName + ' ' + userInfo.lastName, null, null);
                Observer.observeAction('Logged into account', {});
                Observer.observePageView();
            });
        });
    };
};

const onSuccessfulSignUp = (
    userAccountIdentifier : UserAccountId,
    signUpData : ISignUpData,
) : ThunkAction<Promise<void>, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : Promise<void> => {
        let hubspotCookie : string | null = null;
        try {
            hubspotCookie = extraArguments.services.cookieReader.getCookie('hubspotutk');
        } catch (error) {
            if (!(error instanceof UnknownCookieException)) {
                throw error;
            }
        }

        return Promise.all([
            extraArguments.services.unauthenticatedUserSessionManager.getOrCreateUnauthenticatedUserSessionId(),
            Observer.trackConversion.userCreated({
                email: signUpData.emailAddress,
                phone: signUpData.phone,
                bar_name: signUpData.barName,
                first_name: signUpData.firstName,
                last_name: signUpData.lastName,
                zip_code: signUpData.optionalZipCode,
                subscribe_to_blog: signUpData.subscribeToBlog,
                hubspot_cookie : hubspotCookie,
                is_admin_created: false,
                source_url: window.location.href,
                should_receive_marketing: true,
                creator_user_id: null,
                retailer_id: null,
            })
        ])
        .then((result) => {
            const unauthenticatedUserSessionId : UnauthenticatedUserSessionModel.UnauthenticatedUserSessionId = result[0];
            const userFullName = signUpData.firstName + ' ' + signUpData.lastName;
            Observer.identifyAuthenticated(userAccountIdentifier, userFullName, signUpData.emailAddress, null);
            Observer.observeAction('New User Account Created', {
                referrer: document.referrer,
                page: window.location.pathname,
                unauthenticatedUserSessionId: unauthenticatedUserSessionId.value,
            });

            Observer.identifyUnauthenticated(unauthenticatedUserSessionId);
            Observer.observeAction('Clicked button to submit sign up form', {});
            Observer.observeAction('Successfully Submitted Sign Up Form', {
                userAccountId: userAccountIdentifier.getValue(),
            });
        });
    };
};

const onLogout = () : ThunkAction<Promise<void>, ISignUpAndLoginButtonsStore, {services : SignUpAndLoginButtonsActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : Promise<void> => {
        Observer.observeAction('User logged out', {});

        return extraArguments.services.userSessionManager.terminateSession()
        .then(() => {
            dispatch(onSetUserIconPopoverIsShown(false));
            dispatch(setLoggedInUser(null));
            const unauthenticatedUserSessionId : UnauthenticatedUserSessionModel.UnauthenticatedUserSessionId | null = getState().signUpAndLoginButtonsState.unauthenticatedUserSessionId;
            if (!unauthenticatedUserSessionId) {
                throw new RuntimeException();
            }

            Observer.identifyUnauthenticated(unauthenticatedUserSessionId);
            Observer.observePageView();
        });
    };
};

export const SignUpAndLoginButtonsActions : SignUpAndLoginButtonsActionInterfaces.ISignUpAndLoginButtonsActionCreatorsMapObject = {
    onMount,
    onSetUserIconPopoverIsShown,
    onSuccessfulSignIn,
    onSuccessfulSignUp,
    onLogout,
};
